import { useEffect, useState, type ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import type { User } from '@supabase/supabase-js';
import { supabase } from '../config/supabase';
import { parseUserRole, type UserRole } from '../auth/roles';

export interface AuthGuardProps {
  children: ReactNode;
  requiredRole: UserRole | UserRole[];
  loginPath?: string;
  loadingFallback?: ReactNode;
}

export function AuthGuard({
  children,
  requiredRole,
  loginPath = '/login',
  loadingFallback
}: AuthGuardProps) {
  const [user, setUser] = useState<User | null | undefined>(undefined);

  useEffect(() => {
    supabase.auth.getUser().then(({ data: { user: currentUser }, error }) => {
      setUser(error ? null : currentUser ?? null);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!session) {
        setUser(null);
        return;
      }
      supabase.auth.getUser().then(({ data: { user: currentUser }, error }) => {
        setUser(error ? null : currentUser ?? null);
      });
    });

    return () => subscription.unsubscribe();
  }, []);

  if (user === undefined) {
    return loadingFallback ?? null;
  }

  if (!user) {
    return <Navigate to={loginPath} replace />;
  }

  const role = parseUserRole(user);
  const allowed = Array.isArray(requiredRole) ? requiredRole : [requiredRole];

  if (!role || !allowed.includes(role)) {
    return <Navigate to={loginPath} replace state={{ error: 'unauthorized' }} />;
  }

  return <>{children}</>;
}

export default AuthGuard;
